import _get from 'lodash/get';
import _kebabCase from 'lodash/kebabCase';

import { Google, GoogleTransformers, Pixall, PixallTransformers, sanitizeValue } from 'reaxl-analytics-handlers';

import { getInventoryOwnerById } from '@/analytics/utilities';

const { DataLayerTransformer } = PixallTransformers;

const { inventoryTransformer: googleInventoryTransformer, filterNameTransformer, pageNameTransformer } = GoogleTransformers;

const { getConsumerMetadata } = DataLayerTransformer;

/**
 * @summary Handles Buy My Car Online click on promo card fallback
 *
 */

export async function buyOnlinePromoClick({ data = {}, dataLayer }, option) {

    const { filterValues, sponsoredMake = '' } = data;

    let label = 'cta::buy-my-car-online';
    if (sponsoredMake) {
        label = `cta::oem-buy-online-store::${sponsoredMake}::promo-card`;
    }

    await Pixall.sendClickEvent({
        eventSource: 'promotion',
        eventType: 'click',
        label,
        filterValues,
    }, dataLayer, option);

    return { label };
}

/**
 * @summary Handles Buy My Car Online click on promo card impression
 *
 */

export async function buyOnlinePromoImpression(metadata, option) {

    const { data } = metadata;

    const { filterValues } = data;

    await Pixall.sendImpression(
        metadata,
        {
            eventSource: 'promotion',
            label: 'promo-card-fallback',
            filterValues,
        },
        option
    );

    return {};

}

/**
 * @summary Sends click events for commercial entry point clicks (promo card and body style filter)
 * @events event140
 */
export async function buyOnlineButton({
    data = {},
    dataLayer,
}, option) {
    const {
        eventSource = 'button',
        filterValues,
        sponsoredMake = '',
    } = data;

    const oem = sponsoredMake ? '::' + sponsoredMake : '';
    const cmp = `cta::oem-buy-online${oem}::filter`;

    const extra = {
        cmp,
        es: eventSource,
        et: 'click',
    };

    Pixall.sendClickEvent({
        eventSource,
        eventType: 'click',
        label: cmp,
        filterValues,
        hasListingType: false,
    }, dataLayer, option);

    return extra;
}

/**
 * @summary Sends click events for commercial entry point clicks (promo card and body style filter)
 * @events event134 and 135
 */
export async function commercialEntryPoint({
    data = {},
    dataLayer,
}, option) {
    const { cmp, eventSource, filterValues } = data;

    const extra = {
        cmp,
        es: eventSource,
        et: 'click',
    };

    Pixall.sendClickEvent({
        eventSource,
        eventType: 'click',
        label: cmp,
        filterValues,
        hasListingType: false,
    }, dataLayer, option);

    return extra;
}

/**
 * @summary Sends Google click event for compare button click
 * @events event1
 */
export async function compareListingsClick({
    data = {},
    state,
    dataLayer,
}, option) {
    const { wasSticky, isAddToCompare = false, inventoryId, pixallData } = data;
    const eventResult = 'cta::open::compare';

    if (isAddToCompare) {
        const inventory = getInventoryOwnerById(state, inventoryId);
        const pageId = DataLayerTransformer.getPageId(dataLayer);

        Pixall.sendClickEvent({
            eventSource: 'vehicle',
            eventType: 'click',
            label: 'comp_v',
            listingType: 'alp',
            pageId,
            pageType: 'vdp',
            inventory,
            ...pixallData,
        }, dataLayer, option);
    }

    Google.sendClickEvent({
        eventElement: 'compare',
        eventResult,
    }, option);

    return { cmp: 'compare' + (wasSticky ? '_stky' : '') };
}

/**
 * @summary Sends an Google link track for clicking save search link
 * @cmp sv_srch
 * @events event1
 */
export async function saveSearchClick({
    data = {},
}, option) {
    const { par, isLoggedIn, search, dataLayer } = data;

    const extra = {
        cmp: 'sv_srch',
        par,
        ...data,
    };

    Google.sendClickEvent({
        eventElement: 'search-car',
        eventAction: 'save',
        eventResult: `save_${isLoggedIn ? 'logged-in' : 'quick'}::search`,
        eventData: {
            search: search ? googleInventoryTransformer(search, { target: 'search', dataLayer }) : undefined,
        },
    }, option);

    return extra;
}

/**
 * @summary Sends a Google/Pixall click event on feature code change
 * @cmp featureCodes
 */
export async function featureCodeClick({
    data = {},
    dataLayer,
}, option) {
    const { filterName, featureCodeName } = data;

    const eventValue = featureCodeName.toLowerCase();
    const pageName = _get(dataLayer, 'page.pageName', '');
    Google.sendClickEvent({
        eventElement: pageNameTransformer(pageName) || 'search-car',
        eventAction: 'filter',
        eventResult: `${filterNameTransformer(filterName)}::${sanitizeValue(eventValue)}`,
    }, option);

    Pixall.sendClickEvent({
        eventSource: 'field',
        eventType: 'change',
        label: `${filterNameTransformer(filterName)}::${sanitizeValue(eventValue)}`,
        hasListingType: false,
    }, dataLayer, option);

    return {};
}

/**
 * @summary Sends a Google click event when clear a filter on Your Search area
 * @event srp event 26
 */

export async function clearChipClick({
    data = {},
} = {}, option) {
    const removedValue = _kebabCase(_get(data, 'removedValue', ''));

    Google.sendClickEvent({
        eventElement: 'search-car',
        eventAction: 'filter',
        eventResult: `remove-filter::${removedValue}`,
    }, option);

    return {};
}

/**
 * @summary Sends a Google click event when clear all filters
 * @event srp event 27
 */

export async function clearAllFiltersClick({
    data = {},
} = {}, option) {
    const clearFiltersLabel = sanitizeValue(_get(data, 'clearFiltersLabel', ''));

    Google.sendClickEvent({
        eventElement: 'search-car',
        eventAction: 'filter',
        eventResult: `text-link::${clearFiltersLabel}`,
    }, option);

    return {};
}

/**
 * @summary Sends an Adobe link track for clicking suggested filter chip
 * @cmp suggestedFilter
 * @events event1
 */
export async function suggestedFilterClick({
    data = {},
    event,
}, option) {
    // Replace numbers, commas, and parentheses with an empty string at the string's end.
    // For example: Toyota(12,345) -> Toyota
    const contentText = _get(event, 'target.innerText', '').trim().replace(/\((\d|,)*\)$/g, '');
    const { par } = data;

    const extra = {
        cmp: 'suggestedFilter',
        co_txt_url: contentText,
        par,
        ...data,
    };

    Google.sendClickEvent({
        eventElement: 'search-car',
        eventAction: 'filter',
        eventResult: `suggested-filter::${sanitizeValue(contentText)}`,
    }, option);

    Pixall.sendClickEvent({
        eventSource: 'field',
        eventType: 'change',
        label: `suggested-filter::${sanitizeValue(contentText)}`,
        pageId: 'fyc_srl',
        pageType: 'vlp',
    }, option);

    return extra;
}

/**
 * @summary Sends a Pixall click event clicking a category tab
 * @event srp event 147
 */

export async function searchCategoryClick({
    data = {},
} = {},) {
    const { categoryName } = data;

    Pixall.sendClickEvent({
        eventSource: 'button',
        eventType: 'click',
        label: `category::${categoryName}`,
        pageId: 'fyc_srl',
        pageType: 'vlp',
    });

    return {};
}

export const backToResultsClick = async ({ dataLayer = {}, event }, option) => {
    const textContent = (event?.target?.innerText || event?.target?.textContent || '').trim();
    const linkCopy = _kebabCase(textContent);

    const consumerMetadata = await getConsumerMetadata();

    Google.sendClickEvent({
        eventElement: 'search-car',
        eventAction: 'filter',
        eventResult: `text-link::${linkCopy}`,
        eventNonInteractive: true,
    }, option);

    Pixall.sendClickEvent({
        eventSource: 'link',
        eventType: 'click',
        label: `text-link::${linkCopy}`,
        pageId: 'fyc_lnd_fba',
        pageType: 'search',
        hasListingType: false,
        consumerMetadata,
    }, dataLayer, option);

    return {};
};

/**
 * @summary Sends click events for view nationwide inventory link
 * @events event141
 */
export async function viewNationwideInventoryClick({
    data = {},
    dataLayer,
}, option) {
    const { filterValues } = data;

    const cmp = 'text-link::view-nationwide-inventory';

    const extra = {
        cmp,
        es: 'link',
        et: 'click',
    };

    Pixall.sendClickEvent({
        eventType: 'click',
        label: cmp,
        eventSource: 'link',
        filterValues,
        hasListingType: false,
    }, dataLayer, option);

    return extra;
}

/**
 * @summary Handles Inline Filter on promo card impression
 * @events event 148 - Inline Filter Impression
 */
export async function inlineFilterPromoImpression(metadata, option) {
    const { data = {} } = metadata;

    const { pixallData = {} } = data;

    let label = 'inline-filter::';
    switch (pixallData.cmp) {
        case 'filter-inline-strong':
            label += 'strong-attributes';
            break;
        case 'filter-inline-truck':
            label += 'truck-filter';
            break;
        case 'filter-inline-suv':
            label += 'suv-filter';
            break;
        case 'filter-inline-feature': {
            const { featureName = 'na' } = pixallData;
            label += _kebabCase(featureName);
            break;
        }
        case 'filter-inline-suggested':
        default:
            label += 'suggested-filter';
    }

    await Pixall.sendImpression(
        metadata,
        {
            eventSource: 'personalization',
            label,
            hasListingType: false,
        },
        option
    );

    return {};
}

/**
 * @summary Handles Inline Filter CTA click on promo card
 * @events event 149 - Inline Filter CTA
 */
export async function inlineFilterPromoClick({
    data = {},
    dataLayer,
}, option) {
    const { pixallData = {} } = data;

    let label = 'cta::';
    switch (pixallData.cmp) {
        case 'filter-inline-strong':
            label += 'strong-attributes-inline-filter';
            break;
        case 'filter-inline-truck':
            label += 'truck-inline-filter';
            break;
        case 'filter-inline-suv':
            label += 'suv-inline-filter';
            break;
        case 'filter-inline-feature': {
            const { featureName = 'na', isNoPreference = false } = pixallData;
            const preferenceLabel = isNoPreference ? 'no-preference' : 'yes-update';
            label += `inline-filter::${_kebabCase(featureName)}::${preferenceLabel}`;
            break;
        }
        case 'filter-inline-suggested':
        default:
            label += 'suggested-inline-filter';
    }

    await Pixall.sendClickEvent({
        eventSource: 'personalization',
        eventType: 'click',
        label,
        hasListingType: false,
    }, dataLayer, option);

    return { label };
}

/**
 * @summary Sends Pixall event for Best Deals SRP entry point
 */
export async function bestDealClick({ dataLayer = {} }, option) {

    Pixall.sendClickEvent({
        pageType: 'vlp',
        eventSource: 'link',
        label: 'cta::shop-best-deals',
    }, dataLayer, option);

    return {};
}

export default {
    buyOnlinePromoClick,
    buyOnlinePromoImpression,
    buyOnlineButton,
    commercialEntryPoint,
    compareListingsClick,
    saveSearchClick,
    featureCodeClick,
    clearChipClick,
    clearAllFiltersClick,
    suggestedFilterClick,
    searchCategoryClick,
    backToResultsClick,
    viewNationwideInventoryClick,
    inlineFilterPromoImpression,
    inlineFilterPromoClick,
    bestDealClick,
};
